import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'wouter';
import { useAuth } from '../auth/useAuth';
import { routes } from '../lib/assessment/routes';
// V2-VS-01B: every control here is now the shared Button primitive and every
// colour comes from a token. The mode toggles compose from `ghost` per
// Appendix B item 3, which has no legacy analogue because legacy rendered
// Clerk's own components in this position.
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * Minimal, unpolished auth status + sign-in / sign-up / sign-out UI.
 *
 * This proves the browser auth boundary only: it reflects signed-in / signed-out
 * / loading state and lets a user sign in, sign up, or sign out via Supabase Auth
 * (through the auth store). It does NOT read app data, protect routes, or do any
 * product-flow work. Tokens/sessions are never logged.
 */
type Mode = 'sign-in' | 'sign-up';

export type AuthStatusProps = {
  /**
   * Where to send the visitor after a successful sign-in or sign-up.
   *
   * **For application entry points and password-reset completion only. Never
   * for a protected-resource gate.**
   *
   * This component is rendered in eleven places, and the right destination
   * differs at every one of them. Three are entry points — the marketing
   * header, its mobile panel, and the application header — where the visitor
   * came to sign in and has no other errand; those pass this prop. The rest are
   * gates in front of something the visitor already asked for: `ReportPage`,
   * `ReportPrint`, `WorkbookPage`, `ReportLibrary`, `ResultView`, and
   * `RequireAuth`. Signing in there means "show me the thing I opened", and a
   * redirect would take it away — someone who follows a link to
   * `/reports/abc123` and signs in must land on that report, not on a dashboard.
   *
   * `ResultsReady` and `Welcome` are gates too, and they already own their own
   * post-sign-in routing: the pending anonymous assessment has to be claimed
   * before anything else can happen, so they navigate to `/welcome` and then to
   * the result. A redirect from here would strand that claim.
   *
   * Omitted by default, which is the behaviour every call site had before this
   * prop existed. That is deliberate: a new gate that forgets about redirects
   * gets the safe answer rather than the surprising one.
   */
  redirectTo?: string;
};

export function AuthStatus({ redirectTo }: AuthStatusProps = {}) {
  const { status, user, signInWithPassword, signUp, signOut } = useAuth();
  const [, navigate] = useLocation();
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === 'loading') {
    return <p className="text-muted-foreground">Checking session…</p>;
  }

  if (status === 'signed-in') {
    return (
      <div className="flex items-center gap-3">
        <span className="text-muted-foreground">
          Signed in{user?.email ? ` as ${user.email}` : ''}.
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            void signOut();
          }}
        >
          Sign out
        </Button>
      </div>
    );
  }

  // A recovery session is not signed in, so it would fall through to the form
  // below unless it is named here, and showing a sign-in form to someone midway
  // through a reset is misleading. Two controls, because either outcome has to
  // be reachable: finish the reset, or sign out to abandon it.
  if (status === 'password-recovery') {
    return (
      <div className="flex items-center gap-3">
        <Link
          href={routes.updatePassword}
          className="text-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Finish setting your new password
        </Link>
        {/* The escape path. Signing out ends the recovery session and clears the
            persisted marker, returning the user to the ordinary sign-in form.
            Without it this state has no exit, because the sign-in form is not
            rendered here — do not remove it. */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="recovery-sign-out"
          onClick={() => {
            void signOut();
          }}
        >
          Sign out
        </Button>
      </div>
    );
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    // Resolved inside the try, navigated after it. Navigating from within the
    // `finally` would race the `setBusy(false)` that has to run whatever
    // happened, and navigating before it would unmount this form mid-update.
    let destination: string | null = null;

    try {
      const { data, error: authError } =
        mode === 'sign-in'
          ? await signInWithPassword({ email, password })
          : await signUp({ email, password });

      if (authError) {
        setError(authError.message);
      } else if (redirectTo && data.session) {
        // The session check is what makes this correct for sign-up as well as
        // sign-in. Email confirmation is disabled today, so `signUp` returns a
        // live session and the visitor is signed in immediately. If it is ever
        // enabled, `signUp` succeeds with no session — and sending someone who
        // has not confirmed their address to an authenticated page would show
        // them a sign-in form as their reward for signing up.
        destination = redirectTo;
      }
    } finally {
      setBusy(false);
    }

    if (destination) navigate(destination);
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        void onSubmit(event);
      }}
    >
      <div className="flex gap-2 text-sm">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-pressed={mode === 'sign-in'}
          className={
            mode === 'sign-in'
              ? 'font-semibold text-primary underline'
              : 'text-muted-foreground'
          }
          onClick={() => setMode('sign-in')}
        >
          Sign in
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-pressed={mode === 'sign-up'}
          className={
            mode === 'sign-up'
              ? 'font-semibold text-primary underline'
              : 'text-muted-foreground'
          }
          onClick={() => setMode('sign-up')}
        >
          Sign up
        </Button>
      </div>
      <Input
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="Email"
        autoComplete="email"
        required
      />
      <Input
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        placeholder="Password"
        autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
        required
      />
      <Button type="submit" disabled={busy}>
        {busy ? 'Working…' : mode === 'sign-in' ? 'Sign in' : 'Sign up'}
      </Button>
      {mode === 'sign-in' ? (
        <p className="text-xs text-muted-foreground">
          <Link
            href={routes.resetPassword}
            className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid="forgot-password-link"
          >
            Forgot your password?
          </Link>
        </p>
      ) : null}
      {error ? <p className="text-destructive">{error}</p> : null}
    </form>
  );
}
